import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CountryMetricGroup } from '../types/tvd-contract-types';
import { CostAssumptionsLayout, MetricConfig } from '../types/tvd-types';

/**
 * Inputs required to build the cost assumption form structure.
 *
 * The builder stays stateless: callers provide the resolved layout and the
 * display configuration, and the builder returns the correct FormGroup shape.
 */
export interface TvdCostAssumptionsFormBuildOptions {
  layout: CostAssumptionsLayout;
  metricsConfig: MetricConfig[];
  countriesDisplayConfig?: CountryMetricGroup[];
}

@Injectable({ providedIn: 'root' })
export class TvdCostAssumptionsFormBuilder {

  constructor(private fb: FormBuilder) {}

  /**
   * Builds the top-level form structure for the active layout.
   *
   * - flat layout: returns a single FormGroup with one control per metric
   * - byCountry layout: returns a parent FormGroup with one child FormGroup
   *   per country code
   */
  build(options: TvdCostAssumptionsFormBuildOptions): FormGroup {
    const {
      layout,
      metricsConfig,
      countriesDisplayConfig = []
    } = options;

    if (layout === 'byCountry') {
      return this.buildByCountryFormGroup(metricsConfig, countriesDisplayConfig);
    }

    return this.buildFlatFormGroup(metricsConfig);
  }

  /**
   * Builds a single flat FormGroup where each metric ID maps to one control.
   * Two-factor metrics are expanded so each leaf input gets its own control.
   */
  private buildFlatFormGroup(metricsConfig: MetricConfig[]): FormGroup {
    const controls: { [key: string]: FormControl } = {};

    metricsConfig?.forEach(metricConfig => {
      metricConfig.metrics.forEach(metric => {
        if (metric.isTwoFactorsApplicable) {
          (metric.twoFactorMetrics ?? []).forEach(twoFactorMetric => {
            if (twoFactorMetric?.id) {
              controls[twoFactorMetric.id] = new FormControl('');
            }
          });
        } else {
          controls[metric.id] = new FormControl('');
        }
      });
    });

    return this.fb.group(controls);
  }

  /**
   * Builds a nested FormGroup for by-country rendering.
   *
   * Each country reuses the flat metric control structure so the template can
   * scope inputs with [formGroupName] while the payload builder still sees the
   * expected countryCode -> metric map.
   */
  private buildByCountryFormGroup(
    metricsConfig: MetricConfig[],
    countriesDisplayConfig: CountryMetricGroup[]
  ): FormGroup {
    const countryGroups: { [countryCode: string]: FormGroup } = {};

    for (const country of countriesDisplayConfig) {
      countryGroups[country.countryCode] = this.buildFlatFormGroup(metricsConfig);
    }

    return this.fb.group(countryGroups);
  }
}